import {Component, forwardRef, Input, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {DialogService} from '../dialog/dialog.service';
import {ControlValueAccessor, FormBuilder, FormControl, NG_VALUE_ACCESSOR, ValidatorFn} from '@angular/forms';
import {
    StatisticalProfileComponent,
    StatisticalProfilePodResource,
    StatisticalProfilePodResourceType,
    StatisticalProfileResource,
    StatisticalProfileResourceType
} from '../statistical-profile/statistical-profile.component';
import {PredictiveModelComponent, PredictiveModelResource} from '../predictive-model/predictive-model.component';
import * as _ from 'lodash';
import {PlantStatisticalProfile, PodStatisticalProfile} from 'smag-commons/cfg/hierarchy';
import {ModelType, PredictiveModelInfo} from 'smag-commons/cfg/prediction';

export enum WizardPodCardMode {
    STATISTICAL_PROFILE,
    PREDICTIVE_MODEL
}

@Component({
    selector: 'app-wizard-pod-card',
    templateUrl: './wizard-pod-card.component.html',
    styleUrls: ['./wizard-pod-card.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => WizardPodCardComponent),
            multi: true
        }
    ]
})
export class WizardPodCardComponent implements OnInit, ControlValueAccessor {

    private currValue: any; // either POD/PV/WIND statistical profile or predictive model
    private fn: any;
    readonly StatisticalProfileResourceType = StatisticalProfileResourceType;
    readonly WizardPodCardMode = WizardPodCardMode;
    readonly ModelType = ModelType;

    @ViewChild('setParameter') private setParameterDialog: TemplateRef<any>;

    @Input() mode: WizardPodCardMode;
    @Input() resource: StatisticalProfileResource | PredictiveModelResource;
    @Input() isRequired: boolean;
    @Input() resetValueProvider: (ctx: any) => Promise<any>;

    constructor(private readonly dialogService: DialogService,
                private readonly fb: FormBuilder) {
        this.currValue = null;
        this.fn = null;
    }

    ngOnInit(): void {
    }

    registerOnChange(fn: any): void {
        this.fn = fn;
    }

    registerOnTouched(fn: any): void {
    }

    writeValue(obj: any): void {
        this.currValue = _.cloneDeep(obj);
    }

    openDialog() {
        this.dialogService.open<any, any>({
            template: this.setParameterDialog,
            context: {
                form: this.fb.group({
                    param: new FormControl(this.currValue, this.mkValidators(), [])
                })
            },
        }).closed.then(value => {
            if (typeof value !== 'undefined') {
                this.currValue = value;
                if (this.fn) {
                    this.fn(value);
                }
            }
        });
    }

    get isStatisticalProfile(): boolean {
        return this.mode === WizardPodCardMode.STATISTICAL_PROFILE;
    }

    get statisticalProfileResource(): StatisticalProfileResource {
        return this.resource as StatisticalProfileResource;
    }

    get currentEnergyProfile() {
        switch ((this.resource as StatisticalProfilePodResource).podType) {
            case StatisticalProfilePodResourceType.CONSUMER:
                return 'Consumer';
            case StatisticalProfilePodResourceType.PRODUCER:
                return 'Produced';
            case StatisticalProfilePodResourceType.PROSUMER:
                return 'Prosumer';
            default:
                return '';
        }
    }

    get statisticalProfilePod(): PodStatisticalProfile {
        return this.currValue as PodStatisticalProfile;
    }

    get statisticalProfile(): PlantStatisticalProfile | PodStatisticalProfile {
        return this.resource ? this.currValue as PlantStatisticalProfile | PodStatisticalProfile : null;
    }

    get predictiveModel(): PredictiveModelInfo {
        return this.currValue as PredictiveModelInfo;
    }

    get predictiveModelResource(): PredictiveModelResource {
        return this.resource as PredictiveModelResource;
    }

    private mkValidators(): ValidatorFn[] {
        return this.isStatisticalProfile ?
            [StatisticalProfileComponent.statisticalProfileValidator] :
            [PredictiveModelComponent.predictiveModelValidator];
    }
}
